/**
 * Shared presentational parts for the Outcomes Monitoring Report:
 * conduct-score delta, band gauge, band histogram, detriment mix bar, band pill.
 * All are pure and framework-independent.
 */

import { Link } from 'react-router-dom';
import { cn } from '../../../../../lib/utils';
import {
  BAND_COLORS,
  BAND_LABELS,
  bandOf,
  DETRIMENT_META,
} from '../constants';
import type { Bands, DetrimentMix, DetrimentType } from '../types';
import { computeDelta, fmt, type DeltaParts } from '../utils';

const ARROW: Record<DeltaParts['direction'], string> = { up: '▲', down: '▼', flat: '•' };

/**
 * Conduct-score deltas are coloured (lower is better). Volume deltas are neutral
 * (`neutral`) — a rise can mean a new problem or a new channel (SPEC §9.3).
 * Renders nothing when there is no comparison period.
 */
export function Delta({
  cur,
  prev,
  dp,
  pct,
  neutral,
  className,
}: {
  cur: number;
  prev: number | null | undefined;
  dp?: number;
  pct?: boolean;
  neutral?: boolean;
  className?: string;
}) {
  const parts = computeDelta(cur, prev, { dp, pct });
  if (!parts) return null;
  let color = 'hsl(var(--muted-foreground))';
  if (!neutral && parts.direction !== 'flat') {
    color = parts.direction === 'down' ? BAND_COLORS.low : 'var(--go-bittersweet)';
  }
  return (
    <span
      className={cn('whitespace-nowrap text-xs font-bold tabular-nums', className)}
      style={{ color }}
    >
      {ARROW[parts.direction]} {parts.magnitude}
      {parts.pct ? ` · ${parts.pct}` : ''}
    </span>
  );
}

/** 0–10 track with green/amber/berry zones and a pin at the score. */
export function BandGauge({ score }: { score: number }) {
  const band = bandOf(score);
  const pin = Math.max(1, Math.min(99, score * 10));
  return (
    <div>
      <div className="relative mt-2 flex h-[7px] overflow-hidden rounded">
        <div className="h-full" style={{ width: '36%', background: 'rgba(45,123,92,.22)' }} />
        <div className="h-full" style={{ width: '30%', background: 'rgba(201,157,44,.28)' }} />
        <div className="h-full" style={{ width: '34%', background: 'rgba(157,11,77,.22)' }} />
        <div
          className="absolute h-[13px] w-1 -translate-x-1/2 rounded-sm"
          style={{ top: -3, left: `${pin}%`, background: BAND_COLORS[band] }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[9px] font-bold tracking-wider text-muted-foreground">
        <span>0</span>
        <span>10</span>
      </div>
    </div>
  );
}

/**
 * Vertical 3-bar histogram (Low / Mod / High). Each bar and its count+label live
 * in ONE column so the count always sits directly beneath its bar — 21 under
 * green, the moderate count under amber, the high count under berry — and the
 * bars can never overlap the labels. `max` is shared across cards.
 */
export function BandHistogram({
  bands,
  max,
  height = 48,
}: {
  bands: Bands;
  max: number;
  height?: number;
}) {
  const order: Array<keyof Bands> = ['low', 'mod', 'high'];
  return (
    <div className="grid grid-cols-3 gap-3">
      {order.map((k) => {
        // Clamp to the box height so a bar can never overflow, even if `max` is
        // ever smaller than a band count.
        const h = Math.min(height, Math.max(2, Math.round((bands[k] / (max || 1)) * height)));
        return (
          <div key={k} className="flex min-w-0 flex-col">
            <div className="flex items-end overflow-hidden" style={{ height }}>
              <div
                className="w-full rounded-t-[3px]"
                style={{ height: h, background: BAND_COLORS[k], opacity: 0.85 }}
              />
            </div>
            <div className="mt-1.5 text-sm font-extrabold tabular-nums leading-tight">{fmt(bands[k])}</div>
            <div className="text-[8px] font-extrabold uppercase tracking-wider text-muted-foreground">
              {BAND_LABELS[k]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Horizontal stacked bar of the detriment mix. Segment widths are the share of
 * the three (so the bar fills), while the key states raw incidence — the % of
 * records carrying each type, which is non-exclusive (SPEC §5.3 / §9.3).
 */
export function DetrimentMixBar({
  mix,
  showKey = true,
  keyStyle = 'compact',
  drilldownHref,
}: {
  mix: DetrimentMix;
  showKey?: boolean;
  /** 'full' spells out each type (aggregate row); 'compact' is dot + % (cards). */
  keyStyle?: 'full' | 'compact';
  /**
   * When provided, each key figure links into the records queue filtered to that
   * detriment type (plus whatever else the caller bakes into the href). Omitted
   * on the compact cards, which stay static.
   */
  drilldownHref?: (type: DetrimentType) => string;
}) {
  const raw = [mix.dissatisfaction, mix.strain, mix.harm];
  const sum = raw.reduce((a, b) => a + b, 0) || 1;
  return (
    <div>
      <div className="mt-2.5 flex h-3 overflow-hidden rounded">
        {DETRIMENT_META.map((d, i) => (
          <div key={d.type} style={{ width: `${(raw[i] / sum) * 100}%`, background: d.color }} />
        ))}
      </div>
      {showKey && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {DETRIMENT_META.map((d, i) => {
            const inner = (
              <>
                <i className="h-2 w-2 shrink-0 rounded-full" style={{ background: d.color }} />
                <b className="tabular-nums">{Math.round(raw[i])}%</b>
                {keyStyle === 'full' && (
                  <span className="text-[10px] font-semibold text-muted-foreground">{d.label}</span>
                )}
              </>
            );
            const cls = 'flex items-center gap-1.5 whitespace-nowrap text-xs font-extrabold';
            return drilldownHref ? (
              <Link
                key={d.type}
                to={drilldownHref(d.type)}
                className={cn(cls, 'rounded-sm hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring')}
                aria-label={`View ${d.label} records`}
              >
                {inner}
              </Link>
            ) : (
              <span key={d.type} className={cls}>
                {inner}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** LOW / MODERATE / HIGH badge, tinted background + coloured dot. */
export function BandPill({ score }: { score: number }) {
  const band = bandOf(score);
  const tint =
    band === 'low' ? 'rgba(45,123,92,.12)' : band === 'mod' ? 'rgba(201,157,44,.18)' : 'rgba(157,11,77,.12)';
  const text = band === 'mod' ? '#8a6a12' : BAND_COLORS[band];
  return (
    <span
      className="inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.09em]"
      style={{ background: tint, color: text }}
    >
      <i className="block h-1.5 w-1.5 rounded-full" style={{ background: BAND_COLORS[band] }} />
      {BAND_LABELS[band]}
    </span>
  );
}
